import React, { useState } from "react";
import { Compass, RotateCcw } from "lucide-react";
import { T } from "../context/LanguageContext";

const QUESTIONS = [
  { q: "What energizes you the most?", options: [
    { label: "Leading training sessions on the field", scores: { coaching: 3, officiating: 1 } },
    { label: "Helping people recover and perform", scores: { medicine: 3 } },
    { label: "Finding patterns in numbers and video", scores: { analytics: 3 } },
    { label: "Telling stories about sport", scores: { mediacareer: 3 } },
    { label: "Making deals and building partnerships", scores: { marketing: 2, representation: 2 } },
    { label: "Running a flawless matchday", scores: { operations: 3 } },
  ]},
  { q: "Pick your ideal workplace:", options: [
    { label: "Pitch-side or courtside", scores: { coaching: 2, officiating: 2 } },
    { label: "Clinic or performance lab", scores: { medicine: 3 } },
    { label: "Data desk with three monitors", scores: { analytics: 3 } },
    { label: "Studio, press box or newsroom", scores: { mediacareer: 3 } },
    { label: "Stadium operations center", scores: { operations: 3 } },
    { label: "Boardroom or negotiation table", scores: { representation: 2, marketing: 2 } },
  ]},
  { q: "Your standout strength:", options: [
    { label: "Communication and motivation", scores: { coaching: 2, mediacareer: 2 } },
    { label: "Care and empathy", scores: { medicine: 2 } },
    { label: "Logic and precision", scores: { analytics: 2, officiating: 2 } },
    { label: "Organization under pressure", scores: { operations: 2, marketing: 1 } },
    { label: "Persuasion and negotiation", scores: { representation: 3 } },
  ]},
  { q: "What would you happily study?", options: [
    { label: "Sports science and physiology", scores: { coaching: 2, medicine: 2 } },
    { label: "Statistics and coding", scores: { analytics: 3 } },
    { label: "Business or law", scores: { marketing: 2, representation: 2 } },
    { label: "Journalism and media production", scores: { mediacareer: 3 } },
    { label: "Event and facility management", scores: { operations: 2, officiating: 1 } },
  ]},
];

export const CareerQuiz = ({ careers }) => {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const [done, setDone] = useState(false);

  const answer = (opt) => {
    const next = { ...scores };
    Object.entries(opt.scores).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
    setScores(next);
    if (step + 1 >= QUESTIONS.length) setDone(true);
    else setStep(step + 1);
  };

  const reset = () => { setStep(0); setScores({}); setDone(false); };

  const top = done
    ? [...Object.entries(scores)].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([id]) => careers.find((c) => c.id === id)).filter(Boolean)
    : [];

  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 sm:p-8" data-testid="career-quiz">
      <h3 className="flex items-center gap-2 text-xl font-bold text-slate-900"><Compass className="h-5 w-5 text-[#0055FF]" /> <T>Career Explorer Quiz</T></h3>
      <p className="mt-1 text-sm text-slate-600"><T>Answer 4 quick questions to find your sports-career pathway.</T></p>
      {!done ? (
        <div className="mt-5">
          <p className="text-sm font-bold text-[#0055FF]"><T>Question</T> {step + 1} / {QUESTIONS.length}</p>
          <p className="mt-1 text-base font-bold text-slate-900" data-testid="quiz-question"><T>{QUESTIONS[step].q}</T></p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {QUESTIONS[step].options.map((opt, i) => (
              <button key={i} data-testid={`quiz-option-${i}`} onClick={() => answer(opt)} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-all hover:-translate-y-0.5 hover:border-[#0055FF] hover:text-[#0055FF]">
                <T>{opt.label}</T>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-5" data-testid="quiz-result">
          <p className="text-base font-bold text-slate-900"><T>Your best-fit pathways:</T></p>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {top.map((c, i) => (
              <div key={c.id} data-testid={`quiz-result-${c.id}`} className="rounded-xl border border-blue-200 bg-white p-5">
                <p className="text-xs font-bold uppercase text-[#0055FF]">{i === 0 ? <T>Top match</T> : <T>Also consider</T>}</p>
                <h4 className="mt-1 text-base font-bold text-slate-900"><T>{c.title}</T></h4>
                <p className="mt-2 text-sm text-slate-600"><span className="font-bold"><T>Pathway</T>:</span> <T>{c.pathway}</T></p>
                <p className="mt-2 text-xs font-semibold text-purple-700"><T>Demand</T>: <T>{c.demand}</T></p>
              </div>
            ))}
          </div>
          <button data-testid="quiz-restart" onClick={reset} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]">
            <RotateCcw className="h-4 w-4" /> <T>Retake quiz</T>
          </button>
        </div>
      )}
    </div>
  );
};
